import React, { useState } from 'react';

/**
 * Heatmap table for pinned contracts.
 * Fixed columns (contract details) + scenario columns with color-coded cells.
 * Metric toggle: % return, $ P&L, or multiple.
 */

const SCENARIO_ORDER = [
  '-50', '-40', '-30', '-20', '-15', '-10', '-5', '0',
  '5', '10', '15', '20', '25', '30', '40', '50', '75', '100',
];

function getHeatmapColor(pctReturn) {
  if (pctReturn == null || isNaN(pctReturn)) return {};

  // Color scale based on spec
  if (pctReturn >= 200) return { backgroundColor: '#085041', color: '#ffffff' };
  if (pctReturn >= 100) return { backgroundColor: '#0F6E56', color: '#ffffff' };
  if (pctReturn >= 50) return { backgroundColor: '#1D9E75', color: '#ffffff' };
  if (pctReturn >= 20) return { backgroundColor: '#5DCAA5', color: '#000000' };
  if (pctReturn >= 0) return { backgroundColor: '#9FE1CB', color: '#000000' };
  if (pctReturn >= -30) return { backgroundColor: '#D3D1C7', color: '#000000' };
  if (pctReturn >= -60) return { backgroundColor: '#F09595', color: '#000000' };
  return { backgroundColor: '#791F1F', color: '#ffffff' };
}

function formatMetric(scenario, metric) {
  if (!scenario) return '--';

  switch (metric) {
    case 'pctReturn':
      return `${scenario.pct_return >= 0 ? '+' : ''}${scenario.pct_return.toFixed(1)}%`;
    case 'dollarPL':
      return `${scenario.dollar_pl >= 0 ? '+' : ''}$${scenario.dollar_pl.toFixed(2)}`;
    case 'multiple':
      return `${scenario.multiple.toFixed(2)}x`;
    default:
      return '--';
  }
}

function ExplorerHeatmap({ contracts, activeScenarios }) {
  const [metric, setMetric] = useState('pctReturn');

  const visibleScenarios = SCENARIO_ORDER.filter(s => activeScenarios.has(s));

  // Group contracts by ticker
  const grouped = {};
  for (const c of contracts) {
    if (!grouped[c.ticker]) grouped[c.ticker] = [];
    grouped[c.ticker].push(c);
  }

  const formatScenarioHeader = (s) => {
    const n = Number(s);
    return n > 0 ? `+${n}%` : `${n}%`;
  };

  return (
    <div className="bg-gray-800 rounded-lg p-4">
      <div className="flex items-center justify-between mb-3">
        <h3 className="text-sm font-semibold text-gray-200">
          Scenario Heatmap — {contracts.length} contract{contracts.length !== 1 ? 's' : ''}
        </h3>

        {/* Metric toggle */}
        <div className="flex gap-1">
          {[
            { key: 'pctReturn', label: '% Return' },
            { key: 'dollarPL', label: '$ P&L' },
            { key: 'multiple', label: 'Multiple' },
          ].map(opt => (
            <button
              key={opt.key}
              onClick={() => setMetric(opt.key)}
              className={`px-2 py-1 text-xs rounded transition-colors ${
                metric === opt.key
                  ? 'bg-blue-600 text-white'
                  : 'bg-gray-700 text-gray-300 hover:bg-gray-600'
              }`}
            >
              {opt.label}
            </button>
          ))}
        </div>
      </div>

      <div className="overflow-x-auto">
        <table className="w-full text-xs min-w-max border-collapse">
          <thead>
            {/* Header row */}
            <tr className="border-b border-gray-700">
              {/* Fixed columns */}
              <th className="text-left py-2 px-2 sticky left-0 z-10 bg-gray-800 text-white">Ticker</th>
              <th className="text-right py-2 px-2 bg-gray-800 text-white">Strike</th>
              <th className="text-right py-2 px-2 bg-gray-800 text-white">DTE</th>
              <th className="text-left py-2 px-2 bg-gray-800 text-white">Exp</th>
              <th className="text-right py-2 px-2 bg-gray-800 text-white">Exit</th>
              <th className="text-left py-2 px-2 bg-gray-800 text-white">Moneyness</th>
              <th className="text-right py-2 px-2 bg-gray-800 text-white">IV</th>
              <th className="text-right py-2 px-2 bg-gray-800 text-white" style={{ borderRight: '2px solid #374151' }}>
                Premium
              </th>

              {/* Scenario columns */}
              {visibleScenarios.map(s => (
                <th key={s} className="text-center py-2 px-1.5 bg-gray-800 text-gray-400 whitespace-nowrap">
                  {formatScenarioHeader(s)}
                </th>
              ))}
            </tr>
          </thead>
          <tbody>
            {Object.entries(grouped).map(([ticker, tickerContracts]) => (
              <React.Fragment key={ticker}>
                {/* Ticker group header (if multiple tickers) */}
                {Object.keys(grouped).length > 1 && (
                  <tr className="bg-gray-700/30">
                    <td
                      colSpan={8 + visibleScenarios.length}
                      className="py-1.5 px-2 text-xs font-bold text-gray-300 sticky left-0"
                    >
                      {ticker} — ${tickerContracts[0]?.underlying_price?.toFixed(2)}
                    </td>
                  </tr>
                )}

                {/* Contract rows */}
                {tickerContracts.map((c) => {
                  const exitIv = c.scenarios?.['0']
                    ? c.iv  // IV at 0% move is entry IV (no crush)
                    : c.iv;

                  return (
                    <tr
                      key={`${c.ticker}_${c.strike}_${c.expiration}`}
                      className="border-b border-gray-800 hover:bg-gray-700/20"
                    >
                      {/* Fixed columns */}
                      <td className="py-1.5 px-2 font-medium text-white sticky left-0 bg-gray-800">
                        {c.ticker}
                      </td>
                      <td className="py-1.5 px-2 text-right text-white font-bold">
                        ${c.strike.toFixed(0)}
                      </td>
                      <td className="py-1.5 px-2 text-right text-gray-300">{c.dte}</td>
                      <td className="py-1.5 px-2 text-gray-300">
                        {new Date(c.expiration).toLocaleDateString('en-US', { month: 'numeric', day: 'numeric', year: '2-digit' })}
                      </td>
                      <td className="py-1.5 px-2 text-right text-gray-400">{c.exit_days}d</td>
                      <td className="py-1.5 px-2">
                        <span className={`px-1.5 py-0.5 rounded text-xs ${
                          c.moneyness_pct > 2.5
                            ? 'bg-blue-900/40 text-blue-300'  // ITM
                            : c.moneyness_pct < -2.5
                            ? 'bg-amber-900/40 text-amber-300'  // OTM
                            : 'bg-gray-600 text-gray-300'  // ATM
                        }`}>
                          {c.moneyness_label}
                        </span>
                      </td>
                      <td className="py-1.5 px-2 text-right text-gray-300">
                        {(c.iv * 100).toFixed(1)}%
                      </td>
                      <td className="py-1.5 px-2 text-right text-white" style={{ borderRight: '2px solid #374151' }}>
                        ${c.premium.toFixed(2)}
                      </td>

                      {/* Scenario cells */}
                      {visibleScenarios.map(s => {
                        const scenario = c.scenarios?.[s];
                        const pctReturn = scenario?.pct_return;
                        const cellStyle = getHeatmapColor(pctReturn);

                        return (
                          <td
                            key={s}
                            className="py-1.5 px-1.5 text-center font-medium whitespace-nowrap"
                            style={cellStyle}
                          >
                            {formatMetric(scenario, metric)}
                          </td>
                        );
                      })}
                    </tr>
                  );
                })}
              </React.Fragment>
            ))}
          </tbody>
        </table>
      </div>

      {/* Legend */}
      <div className="flex items-center gap-2 mt-3 text-xs text-gray-500">
        <span>Heatmap:</span>
        <span className="px-2 py-0.5 rounded" style={{ backgroundColor: '#791F1F', color: '#fff' }}>{'<-60%'}</span>
        <span className="px-2 py-0.5 rounded" style={{ backgroundColor: '#F09595', color: '#000' }}>-60%</span>
        <span className="px-2 py-0.5 rounded" style={{ backgroundColor: '#D3D1C7', color: '#000' }}>-30%</span>
        <span className="px-2 py-0.5 rounded" style={{ backgroundColor: '#9FE1CB', color: '#000' }}>0%</span>
        <span className="px-2 py-0.5 rounded" style={{ backgroundColor: '#5DCAA5', color: '#000' }}>+20%</span>
        <span className="px-2 py-0.5 rounded" style={{ backgroundColor: '#1D9E75', color: '#fff' }}>+50%</span>
        <span className="px-2 py-0.5 rounded" style={{ backgroundColor: '#0F6E56', color: '#fff' }}>+100%</span>
        <span className="px-2 py-0.5 rounded" style={{ backgroundColor: '#085041', color: '#fff' }}>+200%</span>
      </div>
    </div>
  );
}

export default ExplorerHeatmap;
